import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Users, UserCheck, MapPin, Briefcase, Star } from 'lucide-react';
import { getFirstName } from '../utils/nameUtils';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('guests'); // 'guests' or 'hosts'
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, [activeTab]);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      // TODO: Replace 'user_1' with actual user ID from context
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/favorites?user_id=user_1&type=${activeTab === 'guests' ? 'guest' : 'host'}`
      );
      const data = await response.json();
      
      // Fetch user details for each favorite
      const favoritesWithDetails = await Promise.all(
        data.map(async (fav) => {
          try {
            const userResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/users/${fav.favorited_user_id}`
            );
            const userData = await userResponse.json();
            return { ...fav, user: userData };
          } catch (error) {
            return { ...fav, user: null };
          }
        })
      );
      
      setFavorites(favoritesWithDetails.filter(f => f.user));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (favoriteId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/favorites/${favoriteId}`, {
        method: 'DELETE'
      });
      setFavorites(favorites.filter(f => f.id !== favoriteId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const renderIncomeBadge = (incomeLevel) => {
    if (!incomeLevel) return null;
    
    // Map legacy 'silver' to 'gold' for backwards compatibility
    const level = incomeLevel === 'silver' ? 'gold' : incomeLevel;

    const badges = {
      gold: (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 shadow-lg" title="Gold - $150k-$500k">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="url(#goldGrad)" stroke="#B8860B" strokeWidth="0.5"/>
            <circle cx="12" cy="12" r="7" fill="url(#goldInner)" />
            <defs>
              <radialGradient id="goldGrad"><stop offset="0%" stopColor="#FFD700" /><stop offset="100%" stopColor="#DAA520" /></radialGradient>
              <radialGradient id="goldInner"><stop offset="0%" stopColor="#FFF8DC" /><stop offset="100%" stopColor="#F0E68C" /></radialGradient>
            </defs>
          </svg>
        </div>
      ),
      diamond: (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-br from-blue-300 via-blue-400 to-blue-500 shadow-lg" title="Diamond - $500k+">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M12 2 L16 8 L22 10 L16 16 L12 22 L8 16 L2 10 L8 8 Z" fill="url(#diamondGrad)" stroke="#1E90FF" strokeWidth="0.5"/>
            <defs>
              <linearGradient id="diamondGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E0F7FF" /><stop offset="50%" stopColor="#87CEEB" /><stop offset="100%" stopColor="#4682B4" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      )
    };

    return badges[level] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-espresso to-black pb-24">
      {/* Header */}
      <div className="glass-panel-luxury sticky top-0 z-10 border-b border-gold/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="gold-button-3d p-2 rounded-lg"
          >
            <ArrowLeft size={24} />
          </button>
          <Heart size={28} className="text-burgundy" fill="currentColor" />
          <h1 className="text-2xl font-cinzel gold-text-luxury">My Favorites</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="glass-panel-luxury p-2 flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('guests')}
            className={`flex-1 py-3 px-4 rounded-lg font-cormorant text-lg transition-all ${
              activeTab === 'guests'
                ? 'gold-button-3d'
                : 'text-cream/60 hover:text-cream hover:bg-black/20'
            }`}
          >
            <Users size={20} className="inline mr-2" />
            Favorite Guests
          </button>
          <button
            onClick={() => setActiveTab('hosts')}
            className={`flex-1 py-3 px-4 rounded-lg font-cormorant text-lg transition-all ${
              activeTab === 'hosts'
                ? 'gold-button-3d'
                : 'text-cream/60 hover:text-cream hover:bg-black/20'
            }`}
          >
            <UserCheck size={20} className="inline mr-2" />
            Favorite Hosts
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 border-4 border-gold/20 border-t-gold rounded-full animate-spin mx-auto"></div>
          </div>
        ) : favorites.length === 0 ? (
          <div className="glass-panel-luxury p-12 text-center">
            <Heart size={64} className="mx-auto mb-4 text-cream/30" />
            <h3 className="text-2xl font-cinzel gold-text-luxury mb-2">
              No {activeTab === 'guests' ? 'Guests' : 'Hosts'} Yet
            </h3>
            <p className="text-cream/70">
              {activeTab === 'guests'
                ? 'Save your favorite guests to quickly find them later.'
                : 'Save your favorite event hosts to stay updated on their events.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((favorite) => (
              <div
                key={favorite.id}
                className="glass-panel-luxury overflow-hidden group"
              >
                {/* Photo */}
                <div 
                  className="relative aspect-[3/4] cursor-pointer"
                  onClick={() => navigate(`/user/${favorite.user.id}`)}
                >
                  <img
                    src={favorite.user.photos?.[0] || favorite.user.photo}
                    alt={favorite.user.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1" onClick={() => navigate(`/user/${favorite.user.id}`)} style={{cursor: 'pointer'}}>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-cormorant gold-text-luxury">{getFirstName(favorite.user.name)}</h3>
                        {favorite.user.age && <span className="text-cream/70">{favorite.user.age}</span>}
                      </div>

                      {/* Badges */}
                      <div className="flex items-center gap-2 mb-2">
                        {favorite.user.membership_tier === 'elite' && (
                          <span className="px-2 py-0.5 bg-gradient-to-r from-gold via-gold-deep to-gold rounded-full text-xs text-black font-semibold flex items-center gap-1">
                            <Star size={10} fill="currentColor" /> ELITE
                          </span>
                        )}
                        {favorite.user.verified && (
                          <span className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/40 rounded-full text-xs text-blue-400">✓</span>
                        )}
                        {renderIncomeBadge(favorite.user.income_verified)}
                      </div>

                      {favorite.user.occupation && (
                        <p className="text-cream/70 text-sm flex items-center gap-2 mb-1">
                          <Briefcase size={12} />
                          {favorite.user.occupation}
                        </p>
                      )}
                      {favorite.user.location && (
                        <p className="text-cream/70 text-sm flex items-center gap-2">
                          <MapPin size={12} />
                          {favorite.user.location}
                        </p>
                      )}
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeFavorite(favorite.id)}
                      className="p-2 hover:bg-burgundy/20 rounded-full transition-all group/btn"
                      title="Remove from favorites"
                    >
                      <Heart
                        size={20}
                        className="text-burgundy group-hover/btn:scale-110 transition-transform"
                        fill="currentColor"
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;